<template>
  <div class="carousel-wrapper">
    <!-- Radio inputs -->
    <input 
      v-for="n in 5" 
      :key="'radio-' + n"
      type="radio" 
      name="slider" 
      :id="'slide' + n"
      :checked="currentSlide === n"
      @change="handleSlideChange(n)"
    >
    
    <!-- Carousel -->
    <div class="carousel" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
      <div class="slides">
        <div v-for="(slide, index) in slides" :key="'slide-' + index" class="slide">
          <slot :name="'slide-' + (index + 1)" style="background-image: url(https://cf.europebet.com/prod/content/sliders/images/a65a62e9-bf52-4f5d-a72d-310979fe2af3.webp);">
          </slot>
        </div>
      </div>
    </div>
    
    <!-- Arrow Navigation -->
    <div class="arrow-nav">
      <div>
        <label 
          v-for="n in 5" 
          :key="'prev-' + n"
          :for="'slide' + getPrevSlide(n)"
          :class="'prev' + n"
          v-show="currentSlide === n"
        >❮</label>
      </div>
      <div>
        <label 
          v-for="n in 5" 
          :key="'next-' + n"
          :for="'slide' + getNextSlide(n)"
          :class="'next' + n"
          v-show="currentSlide === n"
        >❯</label>
      </div>
    </div>
    
    <!-- Dots -->
    <div class="dots">
      <label v-for="n in 5" :key="'dot-' + n" :for="'slide' + n"></label>
    </div>
    
    <!-- Play/Pause Button -->
    <div class="play-pause" v-if="showControls">
      <button @click="togglePlayPause">{{ isPlaying ? '⏸' : '▶' }}</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CarouselSlider',
  
  props: {
    autoplay: {
      type: Boolean,
      default: true
    },
    interval: {
      type: Number,
      default: 3000
    },
    pauseOnHover: {
      type: Boolean,
      default: true
    },
    showControls: {
      type: Boolean,
      default: true
    },
    slides: {
      type: Array,
      default: () => [
        { content: '1' },
        { content: '2' },
        { content: '3' },
        { content: '4' },
        { content: '5' }
      ]
    }
  },
  
  data() {
    return {
      currentSlide: 1,
      isPlaying: false,
      autoplayInterval: null
    }
  },
  
  mounted() {
    if (this.autoplay) {
      this.startAutoplay();
    }
  },
  
  beforeDestroy() {
    this.stopAutoplay();
  },
  
  methods: {
    nextSlide() {
      this.currentSlide = this.currentSlide >= 5 ? 1 : this.currentSlide + 1;
      this.$emit('slide-change', this.currentSlide);
    },
    
    prevSlide() {
      this.currentSlide = this.currentSlide <= 1 ? 5 : this.currentSlide - 1;
      this.$emit('slide-change', this.currentSlide);
    },
    
    goToSlide(slideNumber) {
      this.currentSlide = slideNumber;
      this.$emit('slide-change', this.currentSlide);
    },
    
    startAutoplay() {
      this.autoplayInterval = setInterval(this.nextSlide, this.interval);
      this.isPlaying = true;
      this.$emit('autoplay-start');
    },
    
    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
      this.isPlaying = false;
      this.$emit('autoplay-stop');
    },
    
    togglePlayPause() {
      if (this.isPlaying) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    },
    
    handleSlideChange(slideNumber) {
      this.currentSlide = slideNumber;
      this.stopAutoplay();
      this.$emit('manual-change', slideNumber);
    },
    
    handleMouseEnter() {
      if (this.pauseOnHover && this.isPlaying) {
        clearInterval(this.autoplayInterval);
      }
    },
    
    handleMouseLeave() {
      if (this.pauseOnHover && this.isPlaying) {
        this.autoplayInterval = setInterval(this.nextSlide, this.interval);
      }
    },
    
    getPrevSlide(current) {
      return current === 1 ? 5 : current - 1;
    },
    
    getNextSlide(current) {
      return current === 5 ? 1 : current + 1;
    }
  }
}
</script>

<style scoped>


.carousel-wrapper {
    width: 100%;
    position: relative;
    padding: 20px 0;
}

.carousel {
    position: relative;
    width: 100%;
    height: 500px;
    overflow: hidden;
}

/* Radio buttons */
input[type="radio"] {
    display: none;
}

/* Slides container */
.slides {
    position: relative;
    width: 100%;
    height: 100%;
}

/* Individual slide */
.slide {
    --aspectRatio: calc(3/4 * 100%);
    position: absolute;
    width: 70%;
    height:var(--aspectRatio);
    left: 15%;
    top: 10%;
    background: white;
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 5rem;
    font-weight: bold;
    color: white;
    transition: all 0.5s ease;
    opacity: 0;
    transform: translateX(100%) scale(0.8);
    box-shadow: 0 10px 40px rgba(0,0,0,0.4);
}

/* Slide colors */
.slide:nth-child(1) { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.slide:nth-child(2) { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); }
.slide:nth-child(3) { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
.slide:nth-child(4) { background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%); }
.slide:nth-child(5) { background: linear-gradient(135deg, #fa709a 0%, #fee140 100%); }

/* Active slide (center) */
#slide1:checked ~ .carousel .slide:nth-child(1),
#slide2:checked ~ .carousel .slide:nth-child(2),
#slide3:checked ~ .carousel .slide:nth-child(3),
#slide4:checked ~ .carousel .slide:nth-child(4),
#slide5:checked ~ .carousel .slide:nth-child(5) {
    opacity: 1;
    transform: translateX(0) scale(1);
    z-index: 2;
}

/* Previous slide (left side) */
#slide1:checked ~ .carousel .slide:nth-child(5),
#slide2:checked ~ .carousel .slide:nth-child(1),
#slide3:checked ~ .carousel .slide:nth-child(2),
#slide4:checked ~ .carousel .slide:nth-child(3),
#slide5:checked ~ .carousel .slide:nth-child(4) {
    opacity: 0.6;
    transform: translateX(-85%) scale(0.85);
    z-index: 1;
}

/* Next slide (right side) */
#slide1:checked ~ .carousel .slide:nth-child(2),
#slide2:checked ~ .carousel .slide:nth-child(3),
#slide3:checked ~ .carousel .slide:nth-child(4),
#slide4:checked ~ .carousel .slide:nth-child(5),
#slide5:checked ~ .carousel .slide:nth-child(1) {
    opacity: 0.6;
    transform: translateX(85%) scale(0.85);
    z-index: 1;
}

/* Dots navigation */
.dots {
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 10px;
    z-index: 10;
}

.dots label {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: rgba(255,255,255,0.4);
    cursor: pointer;
    transition: all 0.3s;
    border: 2px solid rgba(255,255,255,0.6);
}

.dots label:hover {
    background: rgba(255,255,255,0.6);
}

/* Active dot */
#slide1:checked ~ .dots label:nth-child(1),
#slide2:checked ~ .dots label:nth-child(2),
#slide3:checked ~ .dots label:nth-child(3),
#slide4:checked ~ .dots label:nth-child(4),
#slide5:checked ~ .dots label:nth-child(5) {
    background: white;
    transform: scale(1.3);
}

/* Arrow buttons */
.arrow-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 0 30px;
    pointer-events: none;
    z-index: 10;
}

.arrow-nav label {
    background: rgba(255, 255, 255, 0.9);
    width: 50px;
    height: 50px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    pointer-events: all;
    font-size: 20px;
    color: #333;
    transition: all 0.3s;
    box-shadow: 0 4px 15px rgba(0,0,0,0.2);
}

.arrow-nav label:hover {
    background: white;
    transform: scale(1.1);
}

/* Play/Pause button */
.play-pause {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 10;
}

.play-pause button {
    background: rgba(255, 255, 255, 0.9);
    border: none;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 16px;
    transition: all 0.3s;
}

.play-pause button:hover {
    background: white;
    transform: scale(1.1);
}

/* Responsive */
@media (max-width: 768px) {
    .carousel {
        height: 400px;
    }
    
    .slide {
        width: 75%;
        left: 12.5%;
        font-size: 3rem;
    }
    
    #slide1:checked ~ .carousel .slide:nth-child(5),
    #slide2:checked ~ .carousel .slide:nth-child(1),
    #slide3:checked ~ .carousel .slide:nth-child(2),
    #slide4:checked ~ .carousel .slide:nth-child(3),
    #slide5:checked ~ .carousel .slide:nth-child(4) {
        transform: translateX(-90%) scale(0.8);
    }
    
    #slide1:checked ~ .carousel .slide:nth-child(2),
    #slide2:checked ~ .carousel .slide:nth-child(3),
    #slide3:checked ~ .carousel .slide:nth-child(4),
    #slide4:checked ~ .carousel .slide:nth-child(5),
    #slide5:checked ~ .carousel .slide:nth-child(1) {
        transform: translateX(90%) scale(0.8);
    }
}

@media (max-width: 480px) {
    .carousel {
        height: 300px;
    }
    
    .slide {
        width: 80%;
        left: 10%;
        font-size: 2rem;
    }
    
    .arrow-nav {
        padding: 0 10px;
    }
    
    .arrow-nav label {
        width: 40px;
        height: 40px;
        font-size: 16px;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/sportsbook/widget/slider.vue